package structs.rpcRequests

import kotlinx.serialization.Serializable

/**
 * Request to fetch a player's [structs.account.UsageLedger] from cloud save via server-extend.
 *
 * @param userId The AccelByte user ID.
 */
@Serializable
data class GetUsageLedgerRequest(val userId: String)

/**
 * Request to persist a player's [structs.account.UsageLedger] to cloud save via server-extend.
 *
 * @param userId The AccelByte user ID.
 * @param ledgerJson JSON-encoded [structs.account.UsageLedger].
 */
@Serializable
data class SaveUsageLedgerRequest(val userId: String, val ledgerJson: String)

/**
 * Server-extend RPC payload for `server.extend.getUsageHistory`.
 *
 * Mirrors [structs.account.GetUsageHistoryRequest] but is its own type
 * so the client-side serializer registers the exact payload shape
 * the server-extend handler expects (vs. the main-server shape used
 * by `usage.history.get` over gRPC). They have the same field set
 * today, but keeping them split means a future divergence (extra
 * field, dropped field, naming change) only touches the bridge it
 * applies to.
 */
@Serializable
data class GetUsageHistoryRequestRpc(
    val userId: String,
    val window: structs.account.UsageWindow = structs.account.UsageWindow.WEEK,
    val limit: Int = 50,
    val cursorEntryId: String? = null,
    val sessionId: String? = null
)

/**
 * Request to read the player's current billing + quota-decision state
 * from server-extend. Powers the live "plan + usage" indicator on the
 * main menu and the pre-game cached gate.
 *
 * @param userId The AccelByte user ID.
 * @param gameMode The mode the player is considering. The handler
 *   routes the [AccountSettings] through [QuotaGate.evaluate] with
 *   `isGameStart = true` so the returned decision matches the one
 *   [proxy.ServerConnector.evaluateRequestGameQuotaGate] would emit.
 *   Defaults to MULTIPLAYER.
 */
@Serializable
data class GetQuotaStatusRequest(
    val userId: String,
    val gameMode: structs.account.GameMode = structs.account.GameMode.MULTIPLAYER
)

/**
 * Wire-friendly discriminator for [structs.account.QuotaDecision]. The
 * sealed class in [QuotaGate] is the source of truth; this enum is the
 * kotlinx-serialization-friendly wire shape that the
 * `server.extend.quotaStatus.get` RPC returns. `SoftWarn` carries the
 * fraction as a separate scalar because sealed-class polymorphism is
 * awkward to round-trip through a generic RPC envelope.
 */
@Serializable
enum class QuotaDecisionKind
{
    /** `QuotaDecision.Allow` — proceed. */
    ALLOW,

    /** `QuotaDecision.SoftWarn` — proceed with a warning overlay. */
    SOFT_WARN,

    /** `QuotaDecision.BlockNewGame` — refuse to start a new game. */
    BLOCK_NEW_GAME,

    /** `QuotaDecision.InterruptAndEnd` — single-player game ends now. */
    INTERRUPT_AND_END
}

/**
 * Response payload for `server.extend.quotaStatus.get`.
 *
 * @property accountSettings The player's current [structs.account.AccountSettings]
 *   (same shape as the existing `getAccountSettings` RPC).
 * @property decisionKind The [QuotaDecisionKind] the gate would emit on
 *   the requested mode + current settings.
 * @property softWarnFraction Populated only when [decisionKind] is
 *   `SOFT_WARN`; the fraction of cap consumed (0.0 .. 1.0).
 * @property softWarnRemainingTokens Populated only when [decisionKind]
 *   is `SOFT_WARN`; the absolute remaining tokens under the cap.
 */
@Serializable
data class GetQuotaStatusResponse(
    val accountSettings: structs.account.AccountSettings,
    val decisionKind: QuotaDecisionKind,
    val softWarnFraction: Double = 0.0,
    val softWarnRemainingTokens: Long = 0L
)
